package com.partejar.app.feature.planos.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.luminance
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarButtonSize
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarIconButton
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.components.clearFocusOnTapOutside
import com.partejar.app.core.ui.components.rememberBringIntoViewOnFocusModifier
import com.partejar.app.core.ui.components.rememberFocusedFieldBoundsModifier
import com.partejar.app.core.ui.components.rememberTapOutsideFocusState
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.planos.data.Plano
import com.partejar.app.feature.planos.data.PlanoInput
import com.partejar.app.core.format.centsDigitsToDecimal
import com.partejar.app.core.format.priceToDigits
import com.partejar.app.core.format.formatBrazilianCurrency
import com.partejar.app.core.ui.components.CurrencyVisualTransformation
import org.koin.compose.koinInject

@Composable
fun PlanosScreen(onBack: () -> Unit, viewModel: PlanosViewModel = koinInject(), startCreating: Boolean = false) {
    val state by viewModel.uiState.collectAsState()
    val action by viewModel.actionState.collectAsState()
    var mode by remember { mutableStateOf<PlanoMode>(if (startCreating) PlanoMode.Create else PlanoMode.List) }
    LaunchedEffect(action) {
        if (action is PlanoActionState.Success) {
            viewModel.clearAction()
            if (startCreating) onBack() else mode = PlanoMode.List
        }
    }
    when (val current = mode) {
        PlanoMode.List -> PlanosListLayout(
            state = state,
            onBack = onBack,
            onCreate = { mode = PlanoMode.Create },
            onPlan = { mode = PlanoMode.Edit(it) },
            onRetry = viewModel::load
        )
        PlanoMode.Create -> PlanoForm(
            title = "Criar plano",
            initial = null,
            action = action,
            onBack = { viewModel.clearAction(); if (startCreating) onBack() else mode = PlanoMode.List },
            onSave = viewModel::create,
            onClearAction = viewModel::clearAction
        )
        is PlanoMode.Edit -> PlanoForm(
            title = "Editar plano",
            initial = current.plan,
            action = action,
            onBack = { viewModel.clearAction(); mode = PlanoMode.List },
            onSave = { input -> viewModel.update(current.plan, input) },
            onClearAction = viewModel::clearAction,
            onSetActive = { viewModel.setActive(current.plan, it) }
        )
    }
}

private sealed interface PlanoMode {
    data object List : PlanoMode
    data object Create : PlanoMode
    data class Edit(val plan: Plano) : PlanoMode
}

@Composable
private fun PlanosListLayout(
    state: PlanosUiState,
    onBack: () -> Unit,
    onCreate: () -> Unit,
    onPlan: (Plano) -> Unit,
    onRetry: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = "Planos e preços",
                subtitle = "Ofertas deste espaço",
                leadingContent = { BackAction(onBack) },
            )
        }
    ) { padding ->
        when (state) {
            PlanosUiState.Loading -> Loading(Modifier.padding(padding))
            is PlanosUiState.Error -> ErrorContent(state.message, onRetry, Modifier.padding(padding).padding(spacing.space24))
            is PlanosUiState.Content -> LazyColumn(
                modifier = Modifier.fillMaxSize().padding(padding),
                contentPadding = PaddingValues(spacing.space16),
                verticalArrangement = Arrangement.spacedBy(spacing.space12)
            ) {
                item {
                    PartejarButton(
                        text = "Criar plano",
                        onClick = onCreate,
                        leadingIcon = { Icon(PartejarIcons.Actions.add, contentDescription = null) }
                    )
                }
                if (state.plans.isEmpty()) {
                    item { EmptyContent() }
                } else {
                    items(state.plans, key = { it.id }) { plan ->
                        PlanoCard(plan, onClick = { onPlan(plan) })
                    }
                }
            }
        }
    }
}

@Composable
private fun PlanoCard(plan: Plano, onClick: () -> Unit) {
    val spacing = PartejarTheme.spacing
    PartejarCard(onClick = onClick, modifier = Modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Text(plan.name, style = MaterialTheme.typography.titleMedium)
                    Text(formatPrice(plan.currentPrice), style = MaterialTheme.typography.titleSmall, color = MaterialTheme.colorScheme.primary)
                }
                PartejarStatusBadge(
                    text = if (plan.active) "Ativo" else "Inativo",
                    tone = if (plan.active) PartejarStatusBadgeTone.SUCCESS else PartejarStatusBadgeTone.NEUTRAL
                )
            }
            plan.services.forEach { service ->
                Text("• $service", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }
    }
}

@Composable
private fun PlanoForm(
    title: String,
    initial: Plano?,
    action: PlanoActionState,
    onBack: () -> Unit,
    onSave: (PlanoInput) -> Unit,
    onClearAction: () -> Unit,
    onSetActive: (Boolean) -> Unit = {}
) {
    var name by remember(initial?.id) { mutableStateOf(initial?.name.orEmpty()) }
    var price by remember(initial?.id) {
        mutableStateOf(initial?.currentPrice?.let(::priceToDigits).orEmpty())
    }
    var services by remember(initial?.id) { mutableStateOf(initial?.services.orEmpty()) }
    var serviceDraft by remember { mutableStateOf("") }
    val active = initial?.active ?: true
    var statusChangeRequested by remember(initial?.id) { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }
    val spacing = PartejarTheme.spacing
    val fieldBorderColor = if (MaterialTheme.colorScheme.background.luminance() < 0.5f) {
        MaterialTheme.colorScheme.outlineVariant
    } else {
        MaterialTheme.colorScheme.outline.copy(alpha = 0.55f)
    }
    val focusManager = LocalFocusManager.current
    val priceFocus = remember { FocusRequester() }
    val serviceFocus = remember { FocusRequester() }
    val tapOutsideFocus = rememberTapOutsideFocusState()
    val nameFocusModifier = rememberFocusedFieldBoundsModifier(tapOutsideFocus)
        .then(rememberBringIntoViewOnFocusModifier(validationError))
    val priceFocusModifier = Modifier
        .focusRequester(priceFocus)
        .then(rememberFocusedFieldBoundsModifier(tapOutsideFocus))
        .then(rememberBringIntoViewOnFocusModifier(validationError))
    val serviceFocusModifier = Modifier
        .focusRequester(serviceFocus)
        .then(rememberFocusedFieldBoundsModifier(tapOutsideFocus))
        .then(rememberBringIntoViewOnFocusModifier(validationError))
    val addService: () -> Unit = {
        val service = serviceDraft.trim()
        if (service.isNotEmpty() && services.none { existing -> existing.equals(service, ignoreCase = true) }) {
            services = services + service
            serviceDraft = ""
        }
    }
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = { PartejarTopBar(title, leadingContent = { BackAction(onBack) }) }
    ) { padding ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .clearFocusOnTapOutside(tapOutsideFocus)
                .imePadding()
                .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom)),
            contentPadding = PaddingValues(horizontal = spacing.space16, vertical = spacing.space16),
            verticalArrangement = Arrangement.spacedBy(spacing.space16)
        ) {
            item {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    Text("Informações do plano", style = MaterialTheme.typography.titleMedium)
                    PartejarTextField(
                        name,
                        { name = it },
                        "Nome do plano",
                        modifier = Modifier.then(nameFocusModifier),
                        enabled = action !is PlanoActionState.Submitting,
                        unfocusedBorderColor = fieldBorderColor,
                        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next),
                        keyboardActions = KeyboardActions(onNext = { priceFocus.requestFocus() })
                    )
                    PartejarTextField(
                        value = price,
                        onValueChange = { input ->
                            val digits = input.filter { it in '0'..'9' }.trimStart('0').ifEmpty { if (input.isEmpty()) "" else "0" }
                            if (digits.length <= 12 && (input.isEmpty() || input.any { it in '0'..'9' })) price = digits
                        },
                        visualTransformation = CurrencyVisualTransformation,
                        label = "Valor",
                        placeholder = "0,00",
                        leadingIcon = { Text("R$", style = MaterialTheme.typography.bodyMedium) },
                        modifier = Modifier.then(priceFocusModifier),
                        enabled = action !is PlanoActionState.Submitting,
                        unfocusedBorderColor = fieldBorderColor,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Number,
                            imeAction = ImeAction.Next
                        ),
                        keyboardActions = KeyboardActions(onNext = { serviceFocus.requestFocus() })
                    )
                }
            }
            item {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                        Text("Serviços inclusos", style = MaterialTheme.typography.titleMedium)
                        Text(
                            "Defina os serviços que fazem parte deste plano.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            "A ordem de inclusão será mantida.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    PartejarTextField(
                        value = serviceDraft,
                        onValueChange = { serviceDraft = it },
                        label = "Digite um serviço",
                        modifier = Modifier.then(serviceFocusModifier),
                        enabled = action !is PlanoActionState.Submitting,
                        unfocusedBorderColor = fieldBorderColor,
                        trailingIcon = {
                            PartejarIconButton(onClick = addService, enabled = action !is PlanoActionState.Submitting) {
                                Icon(
                                    PartejarIcons.Actions.add,
                                    contentDescription = "Adicionar serviço"
                                )
                            }
                        },
                        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                        keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus() })
                    )
                }
            }
            items(services, key = { it }) { service ->
                PartejarCard(
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(horizontal = spacing.space12, vertical = spacing.space8)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            service,
                            modifier = Modifier.weight(1f),
                            style = MaterialTheme.typography.bodyLarge
                        )
                        PartejarIconButton(
                            onClick = { services = services - service },
                            enabled = action !is PlanoActionState.Submitting
                        ) {
                            Icon(PartejarIcons.Actions.remove, contentDescription = "Remover serviço: $service",
                                modifier = Modifier.size(PartejarTheme.dimensions.iconMedium), tint = MaterialTheme.colorScheme.error)
                        }
                    }
                }
            }
            if (initial != null) {
                item {
                    PartejarButton(
                        text = if (active) "Desativar plano" else "Ativar plano",
                        onClick = { statusChangeRequested = true },
                        variant = if (active) PartejarButtonVariant.DESTRUCTIVE_SECONDARY else PartejarButtonVariant.SECONDARY,
                        leadingIcon = { Icon(if (active) PartejarIcons.Actions.deactivate else PartejarIcons.Actions.activate, null, Modifier.size(PartejarTheme.dimensions.iconMedium)) },
                        shape = PartejarShapes.pill,
                        enabled = action !is PlanoActionState.Submitting
                    )
                }
            }
            validationError?.let { message ->
                item { PartejarAlert(title = "Revise o plano", tone = PartejarAlertTone.ERROR) { Text(message) } }
            }
            if (action is PlanoActionState.Error) {
                item {
                    PartejarAlert(title = "Não foi possível salvar", tone = PartejarAlertTone.ERROR) {
                        Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                            Text(action.message)
                            PartejarButton("Dispensar", onClearAction, variant = PartejarButtonVariant.TERTIARY, shape = PartejarShapes.pill)
                        }
                    }
                }
            }
            item {
                PartejarButton(
                    text = if (initial == null) "Criar plano" else "Salvar alterações",
                    onClick = {
                        val input = parsePlanoInput(name, centsDigitsToDecimal(price).orEmpty(), active, services)
                        if (input == null) {
                            validationError = "Informe nome, valor válido e ao menos um serviço."
                        } else {
                            validationError = null
                            onSave(input)
                        }
                    },
                    isLoading = action is PlanoActionState.Submitting,
                    leadingIcon = { Icon(if (initial == null) PartejarIcons.Actions.add else PartejarIcons.Actions.save, null, Modifier.size(PartejarTheme.dimensions.iconMedium)) },
                    shape = PartejarShapes.pill
                )
            }
        }
    }
    if (statusChangeRequested && initial != null) {
        AlertDialog(
            onDismissRequest = { statusChangeRequested = false },
            title = { Text(if (active) "Desativar plano?" else "Ativar plano?") },
            text = { Text(if (active) "O plano deixará de ser oferecido em novos contratos. Os contratos existentes serão preservados." else "O plano voltará a ficar disponível para novos contratos.") },
            confirmButton = { PartejarButton(
                text = if (active) "Desativar" else "Ativar",
                onClick = { statusChangeRequested = false; onSetActive(!active) },
                variant = if (active) PartejarButtonVariant.DESTRUCTIVE_SECONDARY else PartejarButtonVariant.PRIMARY,
                leadingIcon = { Icon(if (active) PartejarIcons.Actions.deactivate else PartejarIcons.Actions.activate, null, Modifier.size(PartejarTheme.dimensions.iconSmall)) },
                size = PartejarButtonSize.COMPACT,
                fillWidth = false, shape = PartejarShapes.pill
            ) },
            dismissButton = { PartejarButton("Cancelar", { statusChangeRequested = false }, variant = PartejarButtonVariant.TERTIARY, size = PartejarButtonSize.COMPACT, fillWidth = false, shape = PartejarShapes.pill) }
        )
    }
}

internal fun parsePlanoInput(name: String, price: String, active: Boolean, services: List<String>): PlanoInput? {
    val normalizedServices = services.map { it.trim() }.filter { it.isNotEmpty() }
    val value = price.trim().replace(',', '.').toDoubleOrNull()
    val hasRepeatedService = normalizedServices.map { it.lowercase() }.distinct().size != normalizedServices.size
    return if (name.isBlank() || value == null || !value.isFinite() || value < 0 || normalizedServices.isEmpty() || hasRepeatedService) null
    else PlanoInput(name.trim(), value, active, normalizedServices)
}

internal fun formatPrice(value: Double): String = formatBrazilianCurrency(value)

@Composable
private fun ErrorContent(message: String, onRetry: () -> Unit, modifier: Modifier) = Column(modifier, verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16)) {
    PartejarAlert(title = "Planos indisponíveis", tone = PartejarAlertTone.ERROR) { Text(message) }
    PartejarButton("Tentar novamente", onRetry)
}

@Composable
private fun EmptyContent() = PartejarAlert(title = "Nenhum plano criado", tone = PartejarAlertTone.INFO) {
    Text("Crie o primeiro plano e organize os serviços inclusos.")
}

@Composable
private fun Loading(modifier: Modifier) = LazyColumn(
    modifier = modifier.fillMaxSize(),
    contentPadding = PaddingValues(PartejarTheme.spacing.space16),
    verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
) {
    items(3) {
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                PartejarSkeletonText(Modifier.fillMaxWidth(0.66f).height(PartejarTheme.spacing.space16))
                PartejarSkeletonText(Modifier.fillMaxWidth(0.38f).height(PartejarTheme.spacing.space12))
                PartejarSkeletonText(Modifier.fillMaxWidth().height(PartejarTheme.spacing.space12))
            }
        }
    }
}

@Composable
private fun BackAction(onClick: () -> Unit) = IconButton(onClick = onClick) {
    Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
}

private val previewPlans = listOf(
    Plano(1, "Acompanhamento completo", 350.0, true, listOf("Pré-natal personalizado", "Oficina de preparação", "Suporte contínuo")),
    Plano(2, "Acompanhamento essencial", 220.0, false, listOf("Reuniões de acompanhamento"))
)
private val previewPlanWithManyServices = previewPlans.first().copy(
    services = listOf(
        "Consulta pré-natal",
        "Acompanhamento de parto",
        "Consulta pós-parto",
        "Oficina de preparação para o parto",
        "Suporte para amamentação",
        "Acompanhamento no puerpério"
    )
)

@Preview(name = "Planos · Light", widthDp = 390, heightDp = 844)
@Composable
private fun PlansLightPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    PlanosListLayout(PlanosUiState.Content(previewPlans), {}, {}, {}, {})
}

@Preview(name = "Planos · Dark", widthDp = 390, heightDp = 844)
@Composable
private fun PlansDarkPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    PlanosListLayout(PlanosUiState.Content(previewPlans), {}, {}, {}, {})
}

@Preview(name = "Planos · vazio", widthDp = 390, heightDp = 844)
@Composable
private fun PlansEmptyPreview() = PartejarTheme {
    PlanosListLayout(PlanosUiState.Content(emptyList()), {}, {}, {}, {})
}

@Preview(name = "Criar plano · Light", widthDp = 390, heightDp = 844)
@Composable
private fun CreatePlanLightPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    PlanoForm("Criar plano", null, PlanoActionState.Idle, {}, {}, {})
}

@Preview(name = "Criar plano · Dark", widthDp = 390, heightDp = 844)
@Composable
private fun CreatePlanDarkPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    PlanoForm("Criar plano", null, PlanoActionState.Idle, {}, {}, {})
}

@Preview(name = "Criar plano · compacto 130%", widthDp = 320, heightDp = 640, fontScale = 1.3f)
@Composable
private fun CreatePlanCompactPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    PlanoForm("Criar plano", null, PlanoActionState.Idle, {}, {}, {})
}

@Preview(name = "Editar plano · preenchido", widthDp = 390, heightDp = 844)
@Composable
private fun EditPlanPreview() = PartejarTheme(themeMode = ThemeMode.LIGHT) {
    PlanoForm("Editar plano", previewPlans.first(), PlanoActionState.Idle, {}, {}, {})
}

@Preview(name = "Editar plano · lista longa", widthDp = 320, heightDp = 640)
@Composable
private fun EditPlanLongListPreview() = PartejarTheme(themeMode = ThemeMode.DARK) {
    PlanoForm("Editar plano", previewPlanWithManyServices, PlanoActionState.Idle, {}, {}, {})
}
